import * as React from 'react';
import Button from '@mui/material/Button';
import { alpha, styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import LogoName from 'components/LogoName';
import Logo from 'attrs/logo';
import { useTranslation } from 'react-i18next';
import { ArrowDownward, ArrowForward } from '@mui/icons-material';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '&.MuiDialog-root': {
    pointerEvents: 'none'
  },

  '& .MuiDialog-container': {
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    padding: 16,

    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
      justifyContent: 'center',
      padding: 12
    }
  },

  '& .MuiPaper-root': {
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#4C4D58',
    pointerEvents: 'auto',

    width: '100%',
    maxWidth: 480,
    maxHeight: 'calc(100dvh - 24px)'
  }
}));

export default function ChangeNotifyNameDialog() {
  const { t } = useTranslation();
  const LS_KEY = 'change_notify_name_dialog_dismissed';
  const logoRef = React.useRef(null);
  const logoNameRef = React.useRef(null);
  const [wrapped, setWrapped] = React.useState(false);

  React.useLayoutEffect(() => {
    const checkWrap = () => {
      if (!logoRef.current || !logoNameRef.current) return;

      const logoTop = logoRef.current.offsetTop;
      const nameTop = logoNameRef.current.offsetTop;

      setWrapped(nameTop > logoTop);
    };

    checkWrap();

    window.addEventListener('resize', checkWrap);
    return () => window.removeEventListener('resize', checkWrap);
  }, []);

  const [open, setOpen] = React.useState(() => {
    try {
      return localStorage.getItem(LS_KEY) !== 'true';
    } catch {
      return true;
    }
  });

  const handleClose = () => {
    setOpen(false);
  };

  const handleGotIt = () => {
    try {
      localStorage.setItem(LS_KEY, 'true');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to set localStorage key', e);
    }
    setOpen(false);
  };

  const headerBg = '#4C4D58';
  const accent = '#8CB4C7';
  const text = '#4A4D5C';

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      hideBackdrop
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      disableScrollLock
    >
      <DialogContent
        sx={{
          p: 0,
          width: '100%',
          maxHeight: 'calc(100dvh - 24px)',
          overflowY: 'auto'
        }}
      >
        <Box
          sx={{
            background: headerBg,
            position: 'relative',
            px: { xs: 2.5, sm: 4, md: 5 },
            py: { xs: 3, sm: 5, md: 8 }
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 14 },
              top: { xs: 8, sm: 14 },
              color: 'white',
              opacity: 0.9,
              '&:hover': { background: alpha('#fff', 0.08) }
            }}
          >
            <CloseIcon />
          </IconButton>

          <Stack
            direction={wrapped ? 'column' : 'row'}
            spacing={{ xs: 1.5, sm: 2 }}
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
            sx={{ rowGap: 1 }}
          >
            <Box ref={logoRef} sx={{ display: 'flex', alignItems: 'center' }}>
              <Logo height="35px" style={{ minWidth: '70px' }} />
            </Box>

            {wrapped ? (
              <ArrowDownward sx={{ color: 'white', opacity: 0.9 }} />
            ) : (
              <ArrowForward sx={{ color: 'white', opacity: 0.9 }} />
            )}

            <Box ref={logoNameRef} sx={{ display: 'flex', alignItems: 'center' }}>
              <LogoName />
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            background: '#F5F6F8',
            px: { xs: 2.5, sm: 4, md: 5 },
            py: { xs: 2.5, sm: 3.5, md: 4 }
          }}
        >
          <Typography
            sx={{
              color: text,
              fontSize: { xs: 18, sm: 19, md: 20 },
              fontWeight: 700
            }}
          >
            {t('change_notify_name_dialog.rename_prefix')}{' '}
            <Box component="span" sx={{ color: accent }}>
              {t('change_notify_name_dialog.product_name')}
            </Box>
          </Typography>

          <Box sx={{ height: { xs: 12, sm: 14 } }} />

          <Typography
            sx={{
              color: text,
              fontSize: { xs: 14.5, sm: 15.5, md: 16 },
              lineHeight: 1.6
            }}
          >
            {t('change_notify_name_dialog.body_1')}
            <br />
            {t('change_notify_name_dialog.body_2')}
          </Typography>

          <Box sx={{ height: { xs: 14, sm: 18 } }} />
          {/*
            TODO: we will need it again after creating IRIS Cloud
            <Typography sx={{ color: text, fontSize: 16, lineHeight: 1.6 }}>
              {t('change_notify_name_dialog.new_url_prefix')}{' '}
              <Link
                href="https://pm.iris-cloud.netzsch.com"
                target="_blank"
                rel="noreferrer"
                underline="none"
                sx={{ color: accent, fontWeight: 700 }}
              >
                pm.iris-cloud.netzsch.com
              </Link>
            </Typography>
          */}

          <Box sx={{ height: { xs: 14, sm: 18 } }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleGotIt}
              variant="text"
              sx={{
                color: accent,
                fontWeight: 800,
                letterSpacing: 0.6,
                '&:hover': { background: alpha(accent, 0.08) }
              }}
            >
              {t('change_notify_name_dialog.cta')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
}
